import { useCallback } from 'react';

import { pick } from 'ramda';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { ETransportationType } from 'detailsv2/structure';

import { useDefaultValues } from '../shared';
import { COMMON_FIELDS, FIELDS_BY_TYPE } from './constants';

type Props = {
  getSavedDefaultValues: ReturnType<typeof useDefaultValues>['getSavedDefaultValues'];
  methods: UseFormReturn<FieldValues>;
};

export const useTransportationTypeReset = ({ getSavedDefaultValues, methods }: Props) => {
  const { getValues, reset, watch, setValue } = methods;
  const attachedPage = watch('attachedPage');

  const resetTransportationTypeData = useCallback(
    (newType: ETransportationType) => {
      const currentValues = getValues();

      const relevantFields = FIELDS_BY_TYPE[newType];
      const currentData = currentValues.data || {};
      const currentRelevant = pick(relevantFields, currentData);
      const commonData = pick(COMMON_FIELDS, currentData);

      const finalData = { ...commonData, ...currentRelevant, transportationType: newType };

      reset({ ...currentValues, data: finalData });

      /* Load saved values asynchronously */
      getSavedDefaultValues(attachedPage)
        .then((saved) => {
          if (!saved) return;
          const savedRelevant = pick(relevantFields, saved.data || {});

          /* Add already entered values */
          const mergedData = {
            ...pick(relevantFields, getValues().data),
            ...savedRelevant,
          };

          /* Update only relevant fields */
          Object.entries(mergedData).forEach(([key, value]) => {
            setValue(`data.${key}`, value, { shouldTouch: false, shouldDirty: false });
          });
        })
        .catch((err) => console.error('Error loading defaults', err));
    },
    [getValues, reset, getSavedDefaultValues, attachedPage],
  );

  return {
    resetTransportationTypeData,
  };
};
